import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StatisticsApiService } from '@core/services/statistics-api.service';
import { TranslateService } from '@ngx-translate/core';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  Subject,
  tap,
} from 'rxjs';
import {
  CollectionType,
  EchartsTitleMap,
  EChartsType,
  EchartsTypeMap,
  GroupType,
  StatsChartType,
} from '@pages/statistics-page/constants';
import { ChartUtils } from '@pages/statistics-page/helpers/chart.utils';
import {
  ChartDataResponse,
  ColoredStatisticsGroup,
  EchartSeries,
  GroupData,
  StatsChartConfiguration,
} from '@pages/statistics-page/interfaces';
import { StatisticsMenuValuesService } from './statistics-menu-values.service';

@Injectable()
export class StatisticsDataService {
  private menuFormService = inject(StatisticsMenuValuesService);
  private translate = inject(TranslateService);
  private api = inject(StatisticsApiService);
  private chartGroupType = inject<CollectionType>('chartGroupType' as any);

  readonly chartData$ = new Subject<
    Map<StatsChartType, StatsChartConfiguration>
  >();

  private includeGlobalData = false;
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.includeGlobalData = this.chartGroupType === CollectionType.Score;
    this.listenToMenuFormChanges();
  }

  private getChartConfiguration(): void {
    const pinnedGroups = this.menuFormService.pinnedGroupsValue;
    this.api
      .fetchChartsByType(
        this.menuFormService.pinnedGroupsValue,
        this.menuFormService.activeMenuFiltersValue,
        this.chartGroupType,
        this.includeGlobalData
      )
      .pipe(
        tap(chartData => {
          const displayedGroups = this.includeGlobalData
            ? pinnedGroups
            : pinnedGroups.filter(group => group.type !== GroupType.Global);

          this.chartData$.next(
            this.mapDatasetObj(chartData, displayedGroups) as Map<
              StatsChartType,
              StatsChartConfiguration
            >
          );
        })
      )
      .subscribe();
  }

  /** Method maps ChartGroup array from ChartDataResponse into key-value array
   * where key is chartType and value - data structure used in charts
   */
  private mapDatasetObj(
    chartTypes: ChartDataResponse[],
    pinnedGroups: ColoredStatisticsGroup[]
  ): Map<StatsChartType, unknown> {
    const chartsConfig = new Map<StatsChartType, unknown>();
    chartTypes.forEach(chartTypeItem => {
      const eChartType = EchartsTypeMap[chartTypeItem.chartType];

      chartsConfig.set(
        chartTypeItem.chartType,
        eChartType === 'radar'
          ? this.createRadarChartConfig(chartTypeItem, pinnedGroups)
          : this.createLineOrBarChartConfig(
              chartTypeItem,
              pinnedGroups,
              eChartType
            )
      );
    });

    return chartsConfig;
  }

  private createRadarChartConfig(
    chartTypeItem: ChartDataResponse,
    pinnedGroups: ColoredStatisticsGroup[]
  ): unknown {
    return {
      ...ChartUtils.getGenericChartConfig(
        this.translate.instant(EchartsTitleMap[chartTypeItem.chartType])
      ),
      radar: {
        type: EChartsType.Radar,
        shape: 'polygon',
        indicator: this.getRadarIndicatorConfig(chartTypeItem.labels),
        axisNameGap: 5,
        grid: {
          left: 0,
          top: 0,
          right: 90,
          bottom: 78,
        },
        axisLabel: { show: true, margin: 12, showMinLabel: false },
      },
      color: pinnedGroups.map(group =>
        getComputedStyle(document.documentElement).getPropertyValue(
          group.colorToken
        )
      ),
      series: {
        type: EChartsType.Radar,
        data: chartTypeItem.groupData.map(respGroup => {
          const appGroupData = pinnedGroups.find(g => g.id === respGroup.id);

          return {
            name: appGroupData?.description,
            value: respGroup.values,
            areaStyle: {
              color: ChartUtils.hexToRGBA(
                getComputedStyle(document.documentElement).getPropertyValue(
                  appGroupData?.colorToken || ''
                ),
                0.4
              ),
            },
          };
        }),
      },
    };
  }

  private createLineOrBarChartConfig(
    chartTypeItem: ChartDataResponse,
    pinnedGroups: ColoredStatisticsGroup[],
    eChartType: EChartsType | undefined
  ): unknown {
    return {
      ...ChartUtils.getGenericChartConfig(
        this.translate.instant(EchartsTitleMap[chartTypeItem.chartType])
      ),
      xAxis: {
        type: 'category',
        data: chartTypeItem.labels.map(label => this.translate.instant(label)),
      },
      yAxis: {
        type: 'value',
      },
      grid: {
        left: 68,
        top: 48,
        right: 40,
        bottom: 78,
      },
      labels: chartTypeItem.labels.map(label => this.translate.instant(label)),
      series: this.mapLineOrBarChartSeries(
        pinnedGroups,
        chartTypeItem.groupData,
        eChartType
      ),
    };
  }

  private mapLineOrBarChartSeries(
    pinnedGroups: ColoredStatisticsGroup[],
    responseGroupData: GroupData[],
    eChartType: EChartsType | undefined
  ): EchartSeries[] {
    const series: EchartSeries[] = [];

    responseGroupData.forEach(respGroup => {
      let appGroupData;
      switch (respGroup.type) {
        case GroupType.Department:
          appGroupData = pinnedGroups.find(
            g => g.type === GroupType.Department && g.id === respGroup.id
          );
          break;
        case GroupType.Company:
          appGroupData = pinnedGroups.find(g => g.type === GroupType.Company);
          break;
        case GroupType.Global:
          appGroupData = pinnedGroups.find(g => g.type === GroupType.Global);
          break;
      }

      if (appGroupData) {
        series.push({
          name: appGroupData.description,
          label: appGroupData.description,
          data: respGroup.values,
          color: getComputedStyle(document.documentElement).getPropertyValue(
            appGroupData.colorToken
          ),
          type: eChartType,
          smooth: true,
          labelTranslationKey: appGroupData.labelTranslationKey,
        });
      }
    });

    return series;
  }

  private listenToMenuFormChanges(): void {
    const combined$ = combineLatest([
      this.menuFormService.activeMenuFilters$,
      this.menuFormService.pinnedGroups$,
    ]);

    combined$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        filter(([menuFilters, _]) => {
          return !!menuFilters?.dateFrom && !!menuFilters.dateTo;
        })
      )
      .subscribe(() => {
        this.getChartConfiguration();
      });
  }

  private getRadarIndicatorConfig(labels: string[]): { name: string }[] {
    const indicatorValues: { name: string }[] = [];

    labels.forEach(label => {
      indicatorValues.push({
        name: this.translate.instant(label),
      });
    });

    return indicatorValues;
  }
}
